import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Save, X, Ban } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import api from '@/utils/api';
import { toast } from 'sonner';

const empty = { name: '', display_order: 0, active: true, exclude_from_discounts: false };

export default function CategoriesPage() {
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);

  const fetch = () => { setLoading(true); api.get('/categorias').then(r => setCategories(r.data.categorias)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(fetch, []);

  const save = async () => {
    try {
      if (editing === 'new') {
        await api.post('/categorias', form);
        toast.success('Categoría creada');
      } else {
        await api.put(`/categorias/${editing.id}`, form);
        toast.success('Categoría actualizada');
      }
      setEditing(null);
      fetch();
    } catch (err) { toast.error(err.response?.data?.error || 'Error'); }
  };

  const remove = async (id) => {
    if (!window.confirm('¿Eliminar categoría?')) return;
    try { await api.delete(`/categorias/${id}`); toast.success('Eliminada'); fetch(); }
    catch (err) { toast.error(err.response?.data?.error || 'Error'); }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div data-testid="admin-categories-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold">Categorías</h2>
          <p className="text-sm text-zinc-500">{categories.length} categorías</p>
        </div>
        <button onClick={() => { setEditing('new'); setForm(empty); }} className="flex items-center gap-2 bg-[#E84C1C] text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-[#d43f12] transition-colors" data-testid="cat-add-btn">
          <Plus size={16} /> Nueva
        </button>
      </div>

      {editing && (
        <div className="bg-zinc-900 border border-zinc-800/50 rounded-xl p-4 mb-6 space-y-3" data-testid="cat-form">
          <h3 className="text-sm font-semibold">{editing === 'new' ? 'Nueva categoría' : `Editar: ${editing.name}`}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div><Label className="text-xs text-zinc-500">Nombre</Label><Input value={form.name} onChange={e => set('name', e.target.value)} className="bg-zinc-950 border-zinc-800 h-9 mt-1" data-testid="cat-name" /></div>
            <div><Label className="text-xs text-zinc-500">Orden</Label><Input type="number" value={form.display_order} onChange={e => set('display_order', parseInt(e.target.value) || 0)} className="bg-zinc-950 border-zinc-800 h-9 mt-1" /></div>
            <div className="flex items-end gap-4 pb-1">
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={e => set('active', e.target.checked)} className="accent-[#E84C1C]" /> Activa</label>
            </div>
          </div>

          {/* Exclude from discounts */}
          <div className="p-3 rounded-lg border border-zinc-800/50 bg-zinc-950/50">
            <label className="flex items-start gap-3 cursor-pointer" data-testid="cat-exclude-discounts">
              <input
                type="checkbox"
                checked={form.exclude_from_discounts}
                onChange={e => set('exclude_from_discounts', e.target.checked)}
                className="accent-amber-500 mt-0.5"
              />
              <div>
                <span className="text-sm font-medium flex items-center gap-1.5">
                  <Ban size={14} className="text-amber-500" />
                  Excluir de descuentos y fidelidad
                </span>
                <p className="text-xs text-zinc-500 mt-0.5">Los productos de esta categoría no recibirán descuentos por cupón ni por programa de fidelidad.</p>
              </div>
            </label>
          </div>

          <div className="flex gap-2">
            <button onClick={save} className="flex items-center gap-1 bg-[#E84C1C] text-white text-sm px-4 py-2 rounded-lg hover:bg-[#d43f12]" data-testid="cat-save"><Save size={14} /> Guardar</button>
            <button onClick={() => setEditing(null)} className="flex items-center gap-1 text-sm text-zinc-400 px-3 py-2 rounded-lg hover:bg-zinc-800"><X size={14} /> Cancelar</button>
          </div>
        </div>
      )}

      <div className="bg-zinc-900/50 border border-zinc-800/50 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-zinc-800/50 text-zinc-500 text-xs uppercase tracking-wider">
            <th className="text-left p-3 font-medium">ID</th>
            <th className="text-left p-3 font-medium">Nombre</th>
            <th className="text-center p-3 font-medium">Orden</th>
            <th className="text-center p-3 font-medium">Descuentos</th>
            <th className="text-center p-3 font-medium">Activa</th>
            <th className="text-right p-3 font-medium">Acciones</th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={6} className="p-6 text-center text-zinc-500">Cargando...</td></tr> :
            categories.map(c => (
              <tr key={c.id} data-testid={`cat-row-${c.id}`} className="border-b border-zinc-800/30 hover:bg-zinc-800/20">
                <td className="p-3 text-zinc-500">{c.id}</td>
                <td className="p-3 font-medium">{c.name}</td>
                <td className="p-3 text-center text-zinc-400">{c.display_order || c.sort_order}</td>
                <td className="p-3 text-center">
                  {c.exclude_from_discounts
                    ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 font-medium">Excluida</span>
                    : <span className="text-emerald-400 text-xs">Aplica</span>}
                </td>
                <td className="p-3 text-center">{c.active ? <span className="text-emerald-400 text-xs">Sí</span> : <span className="text-red-400 text-xs">No</span>}</td>
                <td className="p-3 text-right space-x-2">
                  <button onClick={() => { setEditing(c); setForm({ name: c.name, display_order: c.display_order || c.sort_order || 0, active: c.active, exclude_from_discounts: !!c.exclude_from_discounts }); }} className="text-zinc-400 hover:text-[#E84C1C] transition-colors"><Pencil size={14} /></button>
                  <button onClick={() => remove(c.id)} className="text-zinc-400 hover:text-red-400 transition-colors"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
